import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Panel, PanelHeader, PanelHeaderBack, Group, Div, Cell, Button, Spinner,
  FormItem, Input, Select, Text, Search, Headline, ModalRoot,
  ModalPageHeader, IconButton, Checkbox, Snackbar, Avatar,
} from '@vkontakte/vkui';
import { Icon24Add, Icon24Delete, Icon24Write, Icon24Cancel, Icon28Users3Outline, Icon28UserAddOutline, Icon28ArrowDownToSquareOutline, Icon28UploadOutline, Icon28Phone } from '@vkontakte/icons';
import { useWeddingStore } from '../../stores/wedding';
import { getVKFriends, type VKFriend } from '../../api/vkFriends';
import type { Guest, Gender, AgeGroup, SeatingTable } from '../../types';
import { api } from '../../api/client';
import { DragHandle } from '../../ui/DragHandle';
import { ResizableModalPage, sheetId } from '../../ui/ResizableModalPage';

const VK_APP_ID = Number(import.meta.env.VITE_VK_APP_ID || 54822693);

type PickedContact = { name?: string[]; tel?: string[] };
type ContactsPicker = {
  select: (props: string[], opts?: { multiple?: boolean }) => Promise<PickedContact[]>;
};

const CONTACTS_UNSUPPORTED_MSG = 'Выбор контактов работает только в браузере Chrome — используйте «Импорт CSV»';

type FormState = {
  first_name: string;
  last_name: string;
  gender: Gender | '';
  age_group: AgeGroup;
  phone: string;
  email: string;
  address: string;
  note: string;
  is_couple: boolean;
  group_id: string;
  companion_id: string;
  table_id: string;
  menu_id: string;
  list_id: string;
};

const emptyForm: FormState = {
  first_name: '',
  last_name: '',
  gender: '',
  age_group: 'adult',
  phone: '',
  email: '',
  address: '',
  note: '',
  is_couple: false,
  group_id: '',
  companion_id: '',
  table_id: '',
  menu_id: '',
  list_id: '',
};

type SortMode = 'alpha' | 'group' | 'created';

const AGE_LABEL: Record<AgeGroup, string> = {
  adult: 'Взрослый',
  child: 'Ребёнок',
  baby: 'Младенец',
};

const GENDER_LABEL: Record<string, string> = {
  male: 'Мужской',
  female: 'Женский',
  other: 'Другой',
};

function fmtInitials(g: Guest): string {
  const a = (g.first_name || '').trim().charAt(0);
  const b = (g.last_name || '').trim().charAt(0);
  return (a + b).toUpperCase() || '?';
}

const AVATAR_BG = ['#F3D1C8', '#D9D6F2', '#C9E4D4', '#F5E2B8', '#E8C4D8', '#C8D9F0', '#F0D0C0', '#D4E8D0'];

function avatarColor(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  return AVATAR_BG[Math.abs(h) % AVATAR_BG.length];
}

const TAG_BG: Record<string, string> = {
  'Подруга невесты': '#FDE8EC',
  'Родственники невесты': '#E8E4F8',
  'Друзья жениха': '#E4F0E8',
  'Родственники жениха': '#E8E4F8',
  'Коллеги': '#E8EEF8',
  'Соседи': '#F8F0E0',
};

function tagColor(name: string): string {
  return TAG_BG[name] || '#EEF0F5';
}

export default function GuestsPage() {
  const navigate = useNavigate();
  const {
    currentWedding, weddingsLoaded, guests, guestGroups, loadGuests, loadGuestGroups,
    createGuest, updateGuest, deleteGuest, createGuestGroup, deleteGuestGroup,
  } = useWeddingStore();

  const [query, setQuery] = useState('');
  const [filterGroup, setFilterGroup] = useState<string>('');
  const [sortMode, setSortMode] = useState<SortMode>('alpha');
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState<'create' | 'edit' | 'groups' | 'import' | null>(null);
  const [editing, setEditing] = useState<Guest | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [tables, setTables] = useState<SeatingTable[]>([]);
  const [menus, setMenus] = useState<{ id: string; name: string }[]>([]);
  const [lists, setLists] = useState<{ id: string; name: string }[]>([]);
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState<string | null>(null);
  const [openToken, setOpenToken] = useState(0);
  const [confirmDelete, setConfirmDelete] = useState<Guest | null>(null);
  const [newGroup, setNewGroup] = useState('');
  const [exporting, setExporting] = useState(false);
  const [friends, setFriends] = useState<VKFriend[]>([]);
  const [selectedFriends, setSelectedFriends] = useState<Set<number>>(new Set());
  const [friendsLoading, setFriendsLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importGroup, setImportGroup] = useState('');
  const csvInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!currentWedding) {
      if (weddingsLoaded) navigate('/');
      return;
    }
    setLoading(true);
    Promise.all([loadGuests(), loadGuestGroups()]).finally(() => setLoading(false));
    if (currentWedding) {
      const seatBase = `weddings/${currentWedding.id}/seating`;
      api.get<SeatingTable[]>(seatBase).then(setTables).catch(() => {});
      api.get<{ id: string; name: string }[]>(`${seatBase}/menus`).then(setMenus).catch(() => {});
      api.get<{ id: string; name: string }[]>(`${seatBase}/lists`).then(setLists).catch(() => {});
    }
  }, [currentWedding, weddingsLoaded, loadGuests, loadGuestGroups, navigate]);

  useEffect(() => {
    if (!currentWedding) return;
    loadGuests(filterGroup || null);
  }, [filterGroup, currentWedding, loadGuests]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = guests;
    if (q) {
      list = list.filter((g) =>
        `${g.first_name} ${g.last_name || ''} ${g.phone || ''} ${g.email || ''}`.toLowerCase().includes(q),
      );
    }
    const byName = (a: Guest, b: Guest) =>
      `${a.first_name} ${a.last_name || ''}`.localeCompare(`${b.first_name} ${b.last_name || ''}`, 'ru');
    if (sortMode === 'alpha') {
      list = [...list].sort(byName);
    } else if (sortMode === 'group') {
      list = [...list].sort((a, b) => {
        const ga = a.group_id || 'zzz';
        const gb = b.group_id || 'zzz';
        return ga.localeCompare(gb) || byName(a, b);
      });
    } else {
      list = [...list].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
    }
    return list;
  }, [guests, query, sortMode]);

  const guestNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const g of guests) {
      map.set(g.id, `${g.first_name}${g.last_name ? ` ${g.last_name}` : ''}`);
    }
    return map;
  }, [guests]);

  const groupNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const g of guestGroups) map.set(g.id, g.name);
    return map;
  }, [guestGroups]);

  function openCreate() {
    setEditing(null);
    setForm(emptyForm);
    setOpenToken((t) => t + 1);
    setModal('create');
  }

  function openEdit(g: Guest) {
    setEditing(g);
    setForm({
      first_name: g.first_name,
      last_name: g.last_name || '',
      gender: (g.gender as Gender) || '',
      age_group: g.age_group,
      phone: g.phone || '',
      email: g.email || '',
      address: g.address || '',
      note: g.note || '',
      is_couple: g.is_couple,
      group_id: g.group_id || '',
      companion_id: g.companion_id || '',
      table_id: g.table_id || '',
      menu_id: g.menu_id || '',
      list_id: g.list_id || '',
    });
    setOpenToken((t) => t + 1);
    setModal('edit');
  }

  async function handleExportCsv() {
    if (!currentWedding || exporting) return;
    setExporting(true);
    try {
      const token = localStorage.getItem('colibri_token');
      const res = await fetch(`/api/weddings/${currentWedding.id}/guests/export.csv`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error('export failed');
      const blob = await res.blob();
      const filename = `guests-${currentWedding.name || 'wedding'}.csv`;
      try {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        setSnack('CSV скачан');
      } catch {
        const text = await blob.text();
        await navigator.clipboard.writeText(text);
        setSnack('Скачивание недоступно — CSV скопирован в буфер');
      }
    } catch (e) {
      console.error(e);
      setSnack('Не удалось получить CSV');
    } finally {
      setExporting(false);
    }
  }

  async function handleImportCsv(file: File) {
    if (!currentWedding) return;
    setImporting(true);
    try {
      const token = localStorage.getItem('colibri_token');
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`/api/weddings/${currentWedding.id}/guests/import.csv`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      if (!res.ok) throw new Error(`import failed: ${res.status}`);
      const data = await res.json();
      await loadGuests();
      setSnack(`Импортировано: ${data.imported}${data.groups_created ? `, групп: ${data.groups_created}` : ''}`);
    } catch (e) {
      console.error(e);
      setSnack(e instanceof Error ? e.message : 'Не удалось импортировать CSV');
    } finally {
      setImporting(false);
      if (csvInputRef.current) csvInputRef.current.value = '';
    }
  }

  async function handleAddGroup() {
    const name = newGroup.trim();
    if (!name) return;
    try {
      await createGuestGroup(name);
      setNewGroup('');
      setSnack('Группа создана');
    } catch {
      setSnack('Не удалось создать группу');
    }
  }

  async function openFriendsImport() {
    setOpenToken((t) => t + 1);
    setModal('import');
    setFriendsLoading(true);
    try {
      const list = await getVKFriends(VK_APP_ID);
      setFriends(list);
      setSelectedFriends(new Set(list.map((f) => f.id)));
      if (list.length === 0) {
        setSnack('У вас нет друзей для импорта');
      }
    } catch (e) {
      console.error(e);
      const msg = e instanceof Error ? e.message : 'Не удалось загрузить друзей VK';
      setSnack(msg);
      setOpenToken((t) => t + 1);
      setModal(null);
    } finally {
      setFriendsLoading(false);
    }
  }

  function toggleFriend(id: number) {
    setSelectedFriends((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function handleImportFriends() {
    if (importing || selectedFriends.size === 0) return;
    setImporting(true);
    try {
      const picks = friends.filter((f) => selectedFriends.has(f.id));
      for (const f of picks) {
        await createGuest({
          first_name: f.first_name,
          last_name: f.last_name || undefined,
          gender: f.sex === 1 ? 'female' : f.sex === 2 ? 'male' : undefined,
          age_group: 'adult',
          group_id: importGroup || undefined,
        });
      }
      setSnack(`Добавлено гостей: ${picks.length}`);
      setOpenToken((t) => t + 1);
      setModal(null);
      setFriends([]);
      setSelectedFriends(new Set());
    } catch (e) {
      console.error(e);
      setSnack(e instanceof Error ? e.message : 'Ошибка импорта');
    } finally {
      setImporting(false);
    }
  }

  async function handleImportContacts() {
    const picker = (navigator as Navigator & { contacts?: ContactsPicker }).contacts;
    if (!picker || typeof picker.select !== 'function' || !('ContactsManager' in window)) {
      setSnack(CONTACTS_UNSUPPORTED_MSG);
      return;
    }
    if (importing) return;
    setImporting(true);
    try {
      const picked = await picker.select(['name', 'tel'], { multiple: true });
      let added = 0;
      for (const c of picked) {
        const full = (c.name?.[0] || '').trim();
        const [first, ...rest] = full.split(/\s+/).filter(Boolean);
        const tel = c.tel?.[0]?.trim();
        if (!first && !tel) continue;
        await createGuest({
          first_name: first || 'Гость',
          last_name: rest.join(' ') || undefined,
          phone: tel || undefined,
          age_group: 'adult',
        });
        added += 1;
      }
      setSnack(added ? `Добавлено гостей: ${added}` : 'Ничего не выбрано');
    } catch (e) {
      console.error(e);
      if (!(e instanceof Error && e.name === 'AbortError')) {
        setSnack(CONTACTS_UNSUPPORTED_MSG);
      }
    } finally {
      setImporting(false);
    }
  }

  async function handleSave() {
    if (!form.first_name.trim() || saving) return;
    setSaving(true);
    try {
      const payload = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim() || undefined,
        gender: form.gender || undefined,
        age_group: form.age_group,
        phone: form.phone.trim() || undefined,
        email: form.email.trim() || undefined,
        address: form.address.trim() || undefined,
        note: form.note.trim() || undefined,
        is_couple: form.is_couple,
        group_id: form.group_id || undefined,
        companion_id: form.companion_id || undefined,
        table_id: form.table_id || null,
        menu_id: form.menu_id || null,
        list_id: form.list_id || null,
      };
      if (editing) {
        await updateGuest(editing.id, payload);
        setSnack('Гость обновлён');
      } else {
        await createGuest(payload);
        setSnack('Гость добавлен');
      }
      setOpenToken((t) => t + 1);
      setModal(null);
    } catch (e) {
      console.error(e);
      setSnack('Не удалось сохранить');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(g: Guest) {
    try {
      await deleteGuest(g.id);
      setSnack('Гость удалён');
    } catch {
      setSnack('Не удалось удалить');
    } finally {
      setOpenToken((t) => t + 1);
      setConfirmDelete(null);
    }
  }

  return (
    <Panel>
      <PanelHeader before={<PanelHeaderBack onClick={() => navigate('/')} />}>
        Гости
      </PanelHeader>

      {/* Variant 3: compact header + CTA + search + groups carousel + tools */}
      <Group>
        <Div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingTop: 8, paddingBottom: 8 }}>
          <Headline level="1">Гости</Headline>
          <div style={{
            background: 'var(--vkui--color_background_accent)',
            color: 'var(--app-on-accent)', borderRadius: 12, padding: '2px 10px',
            fontSize: 13, fontWeight: 600,
          }}>
            {guests.length}
          </div>
          <div style={{ flex: 1 }} />
          <Button size="m" mode="primary" before={<Icon24Add />} onClick={openCreate}>
            Добавить гостя
          </Button>
        </Div>

        <Search
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Поиск гостей..."
        />

        {/* Groups carousel — labels wrap 2 lines like mockup */}
        <Div style={{ paddingTop: 8, paddingBottom: 4 }}>
          <div
            style={{
              display: 'flex',
              gap: 8,
              overflowX: 'auto',
              paddingBottom: 8,
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'none',
            }}
          >
            <button
              type="button"
              onClick={() => setFilterGroup('')}
              style={{
                flexShrink: 0, height: 'auto', width: 'fit-content',
                whiteSpace: 'normal', lineHeight: 1.2, padding: '8px 14px',
                borderRadius: 20,
                border: filterGroup === '' ? '1px solid var(--app-accent)' : '1px solid var(--vkui--color_separator_primary, rgba(0,0,0,0.12))',
                background: filterGroup === '' ? 'var(--app-accent)' : 'var(--app-card-background)',
                color: filterGroup === '' ? 'var(--app-on-accent)' : 'var(--app-text)',
                fontSize: 13, cursor: 'pointer', textAlign: 'center',
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              Все<br />{guests.length}
            </button>
            {guestGroups.map((g) => {
              const parts = g.name.trim().split(/\s+/);
              const line1 = parts[0] || g.name;
              const line2 = parts.slice(1).join(' ');
              const active = filterGroup === g.id;
              return (
                <button
                  key={g.id}
                  type="button"
                  onClick={() => setFilterGroup(active ? '' : g.id)}
                  style={{
                    flexShrink: 0, height: 'auto', width: 'fit-content',
                    whiteSpace: 'normal', lineHeight: 1.25, padding: '8px 14px',
                    borderRadius: 20,
                    border: active ? '1px solid var(--app-accent)' : '1px solid var(--vkui--color_separator_primary, rgba(0,0,0,0.12))',
                    background: active ? 'var(--app-accent)' : 'var(--app-card-background)',
                    color: active ? 'var(--app-on-accent)' : 'var(--app-text)',
                    fontSize: 13, cursor: 'pointer', textAlign: 'center',
                    WebkitTapHighlightColor: 'transparent',
                  }}
                >
                  {line1}{line2 ? <><br />{line2}</> : null}
                  <br />{g.guest_count ?? 0}
                </button>
              );
            })}
          </div>
        </Div>

        {/* Tools — cards like mockup: icon on top, label below, border per button */}
        <Div style={{ paddingTop: 4, paddingBottom: 12 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            {([
              {
                key: 'groups',
                Icon: Icon28Users3Outline,
                label: 'Группы',
                onClick: () => setModal('groups'),
              },
              {
                key: 'vk',
                Icon: Icon28UserAddOutline,
                label: 'Друзья VK',
                onClick: openFriendsImport,
              },
              {
                key: 'contacts',
                Icon: Icon28Phone,
                label: 'Контакты',
                onClick: () => void handleImportContacts(),
                disabled: importing,
              },
              {
                key: 'import',
                Icon: Icon28ArrowDownToSquareOutline,
                label: 'Импорт CSV',
                onClick: () => csvInputRef.current?.click(),
                disabled: importing,
              },
              {
                key: 'export',
                Icon: Icon28UploadOutline,
                label: 'Экспорт CSV',
                onClick: handleExportCsv,
                disabled: exporting,
              },
            ] as {
              key: string;
              Icon: typeof Icon28Users3Outline;
              label: string;
              onClick: () => void;
              disabled?: boolean;
            }[]).map((t) => (
              <button
                key={t.key}
                type="button"
                disabled={t.disabled}
                onClick={t.onClick}
                style={{
                  flex: 1,
                  minWidth: 0,
                  aspectRatio: '1 / 1',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  padding: '10px 6px',
                  borderRadius: 16,
                  border: '1px solid var(--vkui--color_separator_primary, rgba(0,0,0,0.12))',
                  background: 'var(--vkui--color_background_content, #fff)',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                  cursor: t.disabled ? 'default' : 'pointer',
                  opacity: t.disabled ? 0.55 : 1,
                  WebkitTapHighlightColor: 'transparent',
                }}
              >
                <t.Icon width={28} height={28} style={{ color: 'var(--vkui--color_text_primary, #2C2C2E)' }} />
                <div
                  style={{
                    fontSize: 11,
                    lineHeight: 1.25,
                    color: 'var(--vkui--color_text_secondary)',
                    textAlign: 'center',
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                  }}
                >
                  {t.label}
                </div>
              </button>
            ))}
          </div>
          <input
            ref={csvInputRef}
            type="file"
            accept=".csv,text/csv"
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void handleImportCsv(f);
            }}
          />
        </Div>

        {/* Sort */}
        <Div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 0, paddingBottom: 4 }}>
          <Text style={{ color: 'var(--vkui--color_text_secondary)', fontSize: 13 }}>Сортировка:</Text>
          <Select
            style={{ width: 160 }}
            value={sortMode}
            onChange={(e) => setSortMode(e.target.value as SortMode)}
            options={[
              { label: 'По имени (А–Я)', value: 'alpha' },
              { label: 'По группам', value: 'group' },
              { label: 'По дате добавления', value: 'created' },
            ]}
          />
          <div style={{ flex: 1 }} />
          <Text style={{ color: 'var(--vkui--color_text_secondary)', fontSize: 13 }}>
            Всего: {filtered.length}
          </Text>
        </Div>
      </Group>

      <Group>
        {loading ? (
          <Div style={{ textAlign: 'center', padding: 40 }}><Spinner size="l" /></Div>
        ) : filtered.length === 0 ? (
          <Div style={{ textAlign: 'center', padding: 32 }}>
            <Headline level="2">Пока нет гостей</Headline>
            <Text style={{ marginTop: 8, color: 'var(--vkui--color_text_secondary)' }}>
              Добавьте первого гостя
            </Text>
          </Div>
        ) : (
          filtered.map((g) => {
            const gname = g.group_id ? groupNameById.get(g.group_id) : null;
            return (
              <div
                key={g.id}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: 12,
                  padding: '12px 16px',
                  borderBottom: '1px solid var(--vkui--color_separator_primary, rgba(0,0,0,0.08))',
                }}
              >
                <div style={{
                  width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
                  background: avatarColor(g.id),
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 600, fontSize: 15, color: '#4A4A55',
                }}>
                  {fmtInitials(g)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 600, lineHeight: 1.3 }}>
                    {g.first_name}{g.last_name ? ` ${g.last_name}` : ''}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--vkui--color_text_secondary)', marginTop: 2 }}>
                    {[AGE_LABEL[g.age_group], g.gender ? GENDER_LABEL[g.gender] : null, g.phone]
                      .filter(Boolean).join(' · ') || '—'}
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                    {gname && (
                      <span style={{
                        fontSize: 11, padding: '3px 8px', borderRadius: 10,
                        background: tagColor(gname), color: '#4A4A55',
                      }}>
                        {gname}
                      </span>
                    )}
                    {g.companion_id && (
                      <span style={{
                        fontSize: 11, padding: '3px 8px', borderRadius: 10,
                        background: '#E8E4F8', color: '#4A4A55',
                      }}>
                        Пара: {guestNameById.get(g.companion_id) || '…'}
                      </span>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
                  <IconButton label="Изменить" onClick={() => openEdit(g)}>
                    <Icon24Write />
                  </IconButton>
                  <IconButton label="Удалить" onClick={() => setConfirmDelete(g)}>
                    <Icon24Delete />
                  </IconButton>
                </div>
              </div>
            );
          })
        )}
      </Group>

      <ModalRoot
        activeModal={
          modal === 'groups' ? sheetId('guest-groups', openToken)
            : modal === 'import' ? sheetId('vk-import', openToken)
              : modal ? sheetId('guest-form', openToken) : null
        }
        onClose={() => setModal(null)}
      >
        <ResizableModalPage openToken={openToken} id="guest-form" onClose={() => setModal(null)} settlingHeight={80}>
          <ModalPageHeader>
            {editing ? 'Редактировать гостя' : 'Новый гость'}
          </ModalPageHeader>
        <DragHandle />
          <Group>
            <FormItem top="Имя *">
              <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} placeholder="Анна" />
            </FormItem>
            <FormItem top="Фамилия">
              <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} placeholder="Иванова" />
            </FormItem>
            <FormItem top="Пол">
              <Select
                value={form.gender}
                onChange={(e) => setForm({ ...form, gender: e.target.value as Gender | '' })}
                options={[
                  { label: 'Не указан', value: '' },
                  { label: 'Женский', value: 'female' },
                  { label: 'Мужской', value: 'male' },
                  { label: 'Другой', value: 'other' },
                ]}
              />
            </FormItem>
            <FormItem top="Возраст">
              <Select
                value={form.age_group}
                onChange={(e) => setForm({ ...form, age_group: e.target.value as AgeGroup })}
                options={[
                  { label: 'Взрослый', value: 'adult' },
                  { label: 'Ребёнок', value: 'child' },
                  { label: 'Младенец', value: 'baby' },
                ]}
              />
            </FormItem>
            <FormItem top="Телефон">
              <Input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+7…" />
            </FormItem>
            <FormItem top="Email">
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </FormItem>
            <FormItem top="Адрес">
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            </FormItem>
            <FormItem top="Заметка">
              <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
            </FormItem>
            <FormItem top="Группа">
              <Select
                value={form.group_id}
                onChange={(e) => setForm({ ...form, group_id: e.target.value })}
                options={[
                  { label: 'Без группы', value: '' },
                  ...guestGroups.map((g) => ({ label: g.name, value: g.id })),
                ]}
              />
            </FormItem>
            <FormItem top="Сопровождающий">
              <Select
                value={form.companion_id}
                onChange={(e) => setForm({ ...form, companion_id: e.target.value })}
                options={[
                  { label: 'Нет', value: '' },
                  ...guests
                    .filter((g) => !editing || g.id !== editing.id)
                    .map((g) => ({
                      label: `${g.first_name}${g.last_name ? ` ${g.last_name}` : ''}`,
                      value: g.id,
                    })),
                ]}
              />
            </FormItem>
            <FormItem top="Стол (рассадка)">
              <Select
                value={form.table_id}
                onChange={(e) => setForm({ ...form, table_id: e.target.value })}
                options={[
                  { label: 'Без стола', value: '' },
                  ...tables.map((t) => ({
                    label: t.name,
                    value: t.id,
                  })),
                ]}
              />
            </FormItem>
            <FormItem top="Меню">
              <Select
                value={form.menu_id}
                onChange={(e) => setForm({ ...form, menu_id: e.target.value })}
                options={[
                  { label: 'Не выбрано', value: '' },
                  ...menus.map((m) => ({
                    label: m.name,
                    value: m.id,
                  })),
                ]}
              />
            </FormItem>
            <FormItem top="Список">
              <Select
                value={form.list_id}
                onChange={(e) => setForm({ ...form, list_id: e.target.value })}
                options={[
                  { label: 'Не выбрано', value: '' },
                  ...lists.map((l) => ({
                    label: l.name,
                    value: l.id,
                  })),
                ]}
              />
            </FormItem>
            <Checkbox
              checked={form.is_couple}
              onChange={(e) => setForm({ ...form, is_couple: e.target.checked })}
            >
              Состоит в паре
            </Checkbox>
            <Div>
              <Button size="l" mode="primary" stretched onClick={handleSave} disabled={saving || !form.first_name.trim()}>
                {saving ? 'Сохранение…' : 'Сохранить'}
              </Button>
              <Button size="l" mode="secondary" stretched style={{ marginTop: 8 }} onClick={() => setModal(null)}>
                Отмена
              </Button>
            </Div>
          </Group>
        </ResizableModalPage>
        <ResizableModalPage openToken={openToken} id="guest-groups" onClose={() => setModal(null)} settlingHeight={80}>
          <ModalPageHeader>Группы гостей</ModalPageHeader>
        <DragHandle />
          <Group>
            <Div style={{ display: 'flex', gap: 8 }}>
              <Input
                value={newGroup}
                onChange={(e) => setNewGroup(e.target.value)}
                placeholder="Название группы"
                style={{ flex: 1 }}
              />
              <Button onClick={handleAddGroup} disabled={!newGroup.trim()}>+</Button>
            </Div>
            {guestGroups.length === 0 ? (
              <Div>
                <Text style={{ color: 'var(--vkui--color_text_secondary)' }}>Групп пока нет</Text>
              </Div>
            ) : (
              guestGroups.map((g) => (
                <Cell
                  key={g.id}
                  after={
                    <IconButton
                      label="Удалить"
                      onClick={async () => {
                        await deleteGuestGroup(g.id);
                        setSnack('Группа удалена');
                      }}
                    >
                      <Icon24Delete />
                    </IconButton>
                  }
                >
                  {g.name}
                  <Text style={{ color: 'var(--vkui--color_text_secondary)', fontSize: 12 }}>
                    гостей: {g.guest_count}
                  </Text>
                </Cell>
              ))
            )}
          </Group>
        </ResizableModalPage>
        <ResizableModalPage openToken={openToken} id="vk-import" onClose={() => setModal(null)} settlingHeight={80}>
          <ModalPageHeader>Импорт друзей VK</ModalPageHeader>
        <DragHandle />
          <Group>
            {guestGroups.length > 0 && (
              <FormItem top="Добавить в группу">
                <Select
                  value={importGroup}
                  onChange={(e) => setImportGroup(e.target.value)}
                  options={[
                    { label: 'Без группы', value: '' },
                    ...guestGroups.map((g) => ({ label: g.name, value: g.id })),
                  ]}
                />
              </FormItem>
            )}
            {friendsLoading ? (
              <Div style={{ textAlign: 'center', padding: 32 }}><Spinner size="l" /></Div>
            ) : (
              <>
                <Div style={{ display: 'flex', gap: 8 }}>
                  <Button size="s" onClick={() => setSelectedFriends(new Set(friends.map((f) => f.id)))}>
                    Выбрать всех
                  </Button>
                  <Button size="s" mode="secondary" onClick={() => setSelectedFriends(new Set())}>
                    Снять выбор
                  </Button>
                </Div>
                {friends.length === 0 ? (
                  <Div>
                    <Text>Друзья не найдены или нет доступа</Text>
                  </Div>
                ) : (
                  friends.map((f) => (
                    <Cell
                      key={f.id}
                      before={<Avatar size={28} src={f.photo_100} />}
                      after={
                        <Checkbox
                          checked={selectedFriends.has(f.id)}
                          onChange={() => toggleFriend(f.id)}
                        />
                      }
                    >
                      {f.first_name} {f.last_name}
                    </Cell>
                  ))
                )}
                <Div>
                  <Button
                    size="l"
                    mode="primary"
                    stretched
                    onClick={handleImportFriends}
                    disabled={importing || selectedFriends.size === 0}
                  >
                    {importing ? 'Импорт…' : `Добавить (${selectedFriends.size})`}
                  </Button>
                  <Button size="l" mode="secondary" stretched style={{ marginTop: 8 }} onClick={() => setModal(null)}>
                    Отмена
                  </Button>
                </Div>
              </>
            )}
          </Group>
        </ResizableModalPage>
      </ModalRoot>

      <ModalRoot activeModal={confirmDelete ? sheetId('guest-delete', openToken) : null} onClose={() => setConfirmDelete(null)}>
        <ResizableModalPage openToken={openToken} id="guest-delete" onClose={() => setConfirmDelete(null)} settlingHeight={80}>
          <ModalPageHeader before={<IconButton label="Закрыть" onClick={() => setConfirmDelete(null)}><Icon24Cancel /></IconButton>}>
            Удалить гостя?
          </ModalPageHeader>
        <DragHandle />
          <Group>
            <Div>
              <Text>
                {confirmDelete ? `${confirmDelete.first_name} ${confirmDelete.last_name || ''}`.trim() : ''} будет удалён.
              </Text>
              <Button
                size="l"
                mode="outline"
                stretched
                style={{ marginTop: 16 }}
                onClick={() => confirmDelete && handleDelete(confirmDelete)}
              >
                Удалить
              </Button>
              <Button size="l" mode="secondary" stretched style={{ marginTop: 8 }} onClick={() => setConfirmDelete(null)}>
                Отмена
              </Button>
            </Div>
          </Group>
        </ResizableModalPage>
      </ModalRoot>

      {snack && (
        <Snackbar onClose={() => setSnack(null)} onClosed={() => setSnack(null)}>{snack}</Snackbar>
      )}
    </Panel>
  );
}
